import React, { useState } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { Play, ExternalLink } from 'lucide-react';
import { Button } from '../components/ui/button';

export default function VideosPage() {
  const { t } = useLanguage();
  const [playingVideo, setPlayingVideo] = useState(null);

  const videos = [
    { 
      id: '5WVDgv02sNQ', 
      title: 'Derrama La Lluvia', 
      titleEn: 'Pour Down The Rain',
      description: 'Música y letra: Adlai Alers. Un canto pidiendo la lluvia tardía del Espíritu Santo.',
      descriptionEn: 'Music and lyrics: Adlai Alers. A song asking for the latter rain of the Holy Spirit.',
      views: '12K',
      year: '2024'
    },
    { 
      id: '_qWmyzVo5MU', 
      title: 'Aunque Sea Difícil', 
      titleEn: 'Even If It\'s Difficult',
      description: 'Featuring Michelle Matius. Una canción de esperanza y fortaleza.',
      descriptionEn: 'Featuring Michelle Matius. A song of hope and strength.',
      views: '162K',
      year: '2023'
    },
    { 
      id: 'Pz_D6HZ4vY4', 
      title: 'Mil Gracias (Video Oficial)', 
      titleEn: 'A Thousand Thanks (Official Video)',
      description: 'Del álbum celebrando 30 años de ministerio musical.',
      descriptionEn: 'From the album celebrating 30 years of musical ministry.',
      views: '40K',
      year: '2020'
    },
    { 
      id: 'bhguxEviJbQ', 
      title: 'Amada Sin Medida', 
      titleEn: 'Loved Beyond Measure',
      description: 'Un recordatorio del amor infinito de Dios por cada uno de nosotros.',
      descriptionEn: 'A reminder of God\'s infinite love for each one of us.',
      views: '44K',
      year: '2016'
    },
    { 
      id: 'fpRg-jcKqzQ', 
      title: 'Restaurame', 
      titleEn: 'Restore Me',
      description: 'Una oración de sanidad y restauración a través de Cristo.',
      descriptionEn: 'A prayer of healing and restoration through Christ.',
      views: '41K',
      year: '2014'
    },
    { 
      id: 'OmeBJ7__YTM', 
      title: 'Hoy Te Agradezco', 
      titleEn: 'Today I Thank You',
      description: 'Una expresión de gratitud al Señor por sus bendiciones.',
      descriptionEn: 'An expression of gratitude to the Lord for His blessings.',
      views: '34K',
      year: '2017'
    },
    { 
      id: 'mqAk9OS3U-o', 
      title: 'María Sabías Que', 
      titleEn: 'Mary Did You Know',
      description: 'Cover de la canción clásica navideña con un toque especial.',
      descriptionEn: 'Cover of the classic Christmas song with a special touch.',
      views: '666',
      year: '2024'
    },
    { 
      id: 'd4ynt0ZaNbc', 
      title: 'León De Judá', 
      titleEn: 'Lion of Judah',
      description: 'Video lírico proclamando el poder del León de Judá.',
      descriptionEn: 'Lyric video proclaiming the power of the Lion of Judah.',
      views: '747',
      year: '2024'
    },
    { 
      id: 'HpjB1aN8xi0', 
      title: 'A Mi Me Amó Jesús', 
      titleEn: 'Jesus Loved Me',
      description: 'Video letra de esta hermosa declaración de fe.',
      descriptionEn: 'Lyric video of this beautiful declaration of faith.',
      views: '1.2K',
      year: '2024'
    },
    { 
      id: '-Z5lIdb_I3k', 
      title: 'Cuanto Quisiera', 
      titleEn: 'How I Wish',
      description: 'Video letra de esta hermosa canción de anhelo.',
      descriptionEn: 'Lyric video of this beautiful song of longing.',
      views: '4.6K',
      year: '2024'
    },
    { 
      id: '6q53igZT3TE', 
      title: 'A Mi Me Amó Jesús', 
      titleEn: 'Jesus Loved Me',
      description: 'Una hermosa declaración del amor de Jesús.',
      descriptionEn: 'A beautiful declaration of Jesus\' love.',
      views: '3.3K',
      year: '2015'
    },
    { 
      id: 'Kz-eMJS40CQ', 
      title: 'I\'ll See My Lord / Veré a Jesús', 
      titleEn: 'I\'ll See My Lord',
      description: 'Presentación en la Iglesia Adventista Luso-Brasileña en Newark, NJ.',
      descriptionEn: 'Performance at Luso-Brazilian SDA Church in Newark, NJ.',
      views: '15K',
      year: '2019'
    }
  ];

  return (
    <div className="min-h-screen bg-white" data-testid="videos-page">
      {/* Hero */}
      <section className="pt-32 pb-16 bg-charcoal">
        <div className="container mx-auto px-4 md:px-8">
          <p className="font-lato text-gold-400 text-sm uppercase tracking-[0.3em] mb-2">
            {t('Watch & Listen', 'Ver y Escuchar')}
          </p>
          <h1 className="font-playfair text-5xl md:text-6xl font-bold text-white mb-4">
            {t('Videos', 'Videos')}
          </h1>
          <p className="font-lato text-xl text-gray-400 max-w-2xl">
            {t(
              'Watch the official music videos and lyric videos from Sarah Capeles. Subscribe to the YouTube channel for the latest releases.',
              'Mira los videos musicales oficiales y videos de letras de Sarah Capeles. Suscríbete al canal de YouTube para los últimos lanzamientos.'
            )}
          </p>
          <a 
            href="https://www.youtube.com/sfrodelly" 
            target="_blank" 
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 mt-6"
          >
            <Button className="bg-red-600 hover:bg-red-700 text-white font-lato uppercase tracking-wider">
              <ExternalLink className="w-4 h-4 mr-2" />
              {t('Subscribe on YouTube', 'Suscríbete en YouTube')}
            </Button>
          </a>
        </div>
      </section>

      {/* Videos Grid */}
      <section className="py-16" data-testid="videos-grid">
        <div className="container mx-auto px-4 md:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            {videos.map((video) => (
              <div 
                key={video.id} 
                className="group"
                data-testid={`video-${video.id}`}
              >
                <div className="relative aspect-video bg-gray-900 rounded-lg overflow-hidden mb-4 shadow-lg">
                  {playingVideo === video.id ? (
                    <iframe
                      src={`https://www.youtube.com/embed/${video.id}?autoplay=1`}
                      title={video.title}
                      className="absolute inset-0 w-full h-full"
                      frameBorder="0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                    />
                  ) : (
                    <>
                      <img 
                        src={`https://img.youtube.com/vi/${video.id}/maxresdefault.jpg`}
                        alt={video.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        onError={(e) => {
                          e.target.src = `https://img.youtube.com/vi/${video.id}/hqdefault.jpg`;
                        }}
                      />
                      <div className="absolute inset-0 bg-black/30 flex items-center justify-center">
                        <button 
                          onClick={() => setPlayingVideo(video.id)}
                          className="w-20 h-20 bg-gold-500 rounded-full flex items-center justify-center hover:bg-gold-400 transition-all duration-300 transform hover:scale-110 shadow-xl"
                          data-testid={`play-${video.id}`}
                        >
                          <Play className="w-10 h-10 text-white ml-1" />
                        </button>
                      </div>
                      <div className="absolute bottom-4 right-4 bg-black/70 px-3 py-1 rounded">
                        <span className="font-lato text-white text-sm">{video.views} {t('views', 'vistas')}</span>
                      </div>
                    </>
                  )}
                </div>
                <div className="flex justify-between items-start">
                  <div>
                    <h3 className="font-playfair text-xl font-bold text-charcoal group-hover:text-gold-600 transition-colors">
                      {t(video.titleEn, video.title)}
                    </h3>
                    <p className="font-lato text-gray-600 text-sm mt-2">
                      {t(video.descriptionEn, video.description)}
                    </p>
                    <p className="font-lato text-gray-400 text-sm mt-2">{video.year}</p>
                  </div>
                  <a 
                    href={`https://www.youtube.com/watch?v=${video.id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-gold-500 hover:text-gold-600 transition-colors"
                  >
                    <ExternalLink className="w-5 h-5" />
                  </a>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Playlists Section */}
      <section className="py-16 bg-cream" data-testid="playlists-section">
        <div className="container mx-auto px-4 md:px-8">
          <h2 className="font-playfair text-3xl font-bold text-charcoal mb-8 text-center">
            {t('Playlists', 'Listas de Reproducción')}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <a 
              href="https://www.youtube.com/playlist?list=PLuF3_8NHa4L89LR6gWlHAkX7J4XJJ6YZQ"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition-shadow"
            >
              <h3 className="font-playfair text-lg font-semibold text-charcoal mb-2">
                {t('Peaceful Christian Music', 'Música Cristiana que trae paz')}
              </h3>
              <p className="font-lato text-gray-500 text-sm">76 videos</p>
            </a>
            <a 
              href="https://www.youtube.com/playlist?list=PLuF3_8NHa4L-pM9UeSjuY2sF5XmrLlS5l"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition-shadow"
            >
              <h3 className="font-playfair text-lg font-semibold text-charcoal mb-2">
                {t('Christmas Music', 'Música de Navidad')}
              </h3>
              <p className="font-lato text-gray-500 text-sm">9 videos</p>
            </a>
            <a 
              href="https://www.youtube.com/playlist?list=PLuF3_8NHa4L_ktMGrWrBYglTh4pS71Aj5"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition-shadow"
            >
              <h3 className="font-playfair text-lg font-semibold text-charcoal mb-2">
                {t('En Koinonia with Sarah', 'En Koinonia con Sarah')}
              </h3>
              <p className="font-lato text-gray-500 text-sm">39 videos</p>
            </a>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-charcoal text-center">
        <div className="container mx-auto px-4 md:px-8">
          <h3 className="font-playfair text-3xl font-bold text-white mb-4">
            {t('Don\'t Miss a Video', 'No te pierdas ningún video')}
          </h3>
          <p className="font-lato text-gray-400 mb-8 max-w-xl mx-auto">
            {t(
              'Subscribe to the YouTube channel and turn on notifications to be the first to see new releases.',
              'Suscríbete al canal de YouTube y activa las notificaciones para ser el primero en ver nuevos lanzamientos.'
            )}
          </p>
          <a 
            href="https://www.youtube.com/sfrodelly?sub_confirmation=1" 
            target="_blank" 
            rel="noopener noreferrer"
          >
            <Button className="bg-red-600 hover:bg-red-700 text-white font-lato text-lg uppercase tracking-wider px-8 py-6">
              {t('Subscribe Now', 'Suscríbete Ahora')}
            </Button>
          </a>
        </div>
      </section>
    </div>
  );
}
